import { type MouseEvent, useRef, useState } from 'react';

import {
    computeMarqueeCoveredIds,
    type MarqueeContentRect,
    type MarqueeRect,
} from '@apps/web/lib/marquee_selection';

/** Travel before a press becomes a drag, so a click still reads as a click. */
const DRAG_THRESHOLD_PX = 4;

type Point = { readonly x: number; readonly y: number };

/** Rubber-band selection: the rectangle starts wherever the pointer went
 * down, including the gaps between cells, and takes what it overlaps. */
export function useCoverageMarquee({
    scrollElementRef,
    ignoreOriginSelector,
    onEmptyPress,
    onSelectIds,
}: {
    readonly scrollElementRef: React.RefObject<HTMLDivElement | null>;
    /** Presses starting here keep ordinary clicking and text selection. */
    readonly ignoreOriginSelector?: string;
    /** A press on empty space inside the grid, which selects nothing. */
    readonly onEmptyPress?: () => void;
    readonly onSelectIds: (ids: ReadonlyArray<string>) => void;
}) {
    const dragPointerRef = useRef<number | null>(null);
    const originRef = useRef<Point | null>(null);
    const hitOrderRef = useRef(new Map<string, number>());
    const [marqueeRect, setMarqueeRect] = useState<MarqueeRect | null>(null);
    const frameRef = useRef<number | null>(null);
    const pendingRectRef = useRef<MarqueeRect | null>(null);
    // pointerup clears the rectangle before the click fires, so the cell's
    // own isMarqueeActive guard reads false and would collapse the drag.
    const suppressClickRef = useRef(false);

    const rectFor = (origin: Point, current: Point): MarqueeRect => ({
        left: Math.min(origin.x, current.x),
        top: Math.min(origin.y, current.y),
        width: Math.abs(current.x - origin.x),
        height: Math.abs(current.y - origin.y),
    });

    // Measured once per drag: per-move measuring forces synchronous layout
    // for every cell right after React re-renders the grid.
    const itemRectsRef = useRef<Map<string, MarqueeContentRect>>(new Map());

    const measureCells = (container: HTMLElement) => {
        const containerRect = container.getBoundingClientRect();
        const itemRects = new Map<string, MarqueeContentRect>();
        for (const cell of container.querySelectorAll<HTMLElement>(
            '[data-coverage-cell]',
        )) {
            const id = cell.dataset['cellId'];
            if (id === undefined) {
                continue;
            }
            const box = cell.getBoundingClientRect();
            itemRects.set(id, {
                left: box.left - containerRect.left + container.scrollLeft,
                top: box.top - containerRect.top + container.scrollTop,
                right: box.right - containerRect.left + container.scrollLeft,
                bottom: box.bottom - containerRect.top + container.scrollTop,
            });
        }
        itemRectsRef.current = itemRects;
    };

    const pointIn = (container: HTMLElement, event: React.PointerEvent) => {
        const box = container.getBoundingClientRect();
        return {
            x: event.clientX - box.left + container.scrollLeft,
            y: event.clientY - box.top + container.scrollTop,
        };
    };

    const onPointerDown = (event: React.PointerEvent) => {
        const container = scrollElementRef.current;
        if (container === null || event.button !== 0) {
            return;
        }
        // A second touch mid-drag would otherwise reset the origin.
        if (dragPointerRef.current !== null) {
            return;
        }
        if (
            ignoreOriginSelector !== undefined &&
            (event.target as HTMLElement | null)?.closest(ignoreOriginSelector)
        ) {
            return;
        }
        suppressClickRef.current = false;
        dragPointerRef.current = event.pointerId;
        originRef.current = pointIn(container, event);
        hitOrderRef.current = new Map();
        measureCells(container);
        // Pressing empty space starts fresh rather than adding to the last.
        if (
            (event.target as HTMLElement | null)?.closest(
                '[data-coverage-cell]',
            ) === null
        ) {
            onEmptyPress?.();
        }
    };

    const onPointerMove = (event: React.PointerEvent) => {
        if (dragPointerRef.current !== event.pointerId) {
            return;
        }
        const container = scrollElementRef.current;
        const origin = originRef.current;
        if (container === null || origin === null) {
            return;
        }
        const current = pointIn(container, event);
        const rect = rectFor(origin, current);
        if (
            marqueeRect === null &&
            rect.width < DRAG_THRESHOLD_PX &&
            rect.height < DRAG_THRESHOLD_PX
        ) {
            return;
        }
        suppressClickRef.current = true;
        // A drag leaving the scroll container keeps running and commits.
        container.setPointerCapture(event.pointerId);
        // One update per frame: a pointer emits several moves per frame and
        // each re-renders every cell, which locks the tab up on a wide drag.
        pendingRectRef.current = rect;
        if (frameRef.current !== null) {
            return;
        }
        frameRef.current = requestAnimationFrame(() => {
            frameRef.current = null;
            const pending = pendingRectRef.current;
            if (pending === null) {
                return;
            }
            setMarqueeRect(pending);
            onSelectIds(
                computeMarqueeCoveredIds(
                    pending,
                    itemRectsRef.current,
                    hitOrderRef.current,
                ),
            );
        });
    };

    const endDrag = () => {
        if (frameRef.current !== null) {
            cancelAnimationFrame(frameRef.current);
            frameRef.current = null;
        }
        // A flick can release before the first frame runs, so commit the
        // last move here or the drag selects less than it drew.
        const pending = pendingRectRef.current;
        if (pending !== null) {
            onSelectIds(
                computeMarqueeCoveredIds(
                    pending,
                    itemRectsRef.current,
                    hitOrderRef.current,
                ),
            );
        }
        pendingRectRef.current = null;
        originRef.current = null;
        dragPointerRef.current = null;
        setMarqueeRect(null);
    };

    const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
        if (!suppressClickRef.current) {
            return;
        }
        suppressClickRef.current = false;
        event.preventDefault();
        event.stopPropagation();
    };

    return {
        marqueeRect,
        /** True while a rectangle is being drawn, so clicks can be ignored. */
        isMarqueeActive: marqueeRect !== null,
        marqueeHandlers: {
            onPointerDown,
            onPointerMove,
            onPointerUp: endDrag,
            onPointerCancel: endDrag,
            onClickCapture,
        },
    };
}
